import React, { useState, useEffect } from "react";
import {
  Box,
  SimpleGrid,
  Text,
  useToast,
  Heading,
  VStack,
  HStack,
  Icon,
  Badge,
  Avatar,
  Container,
  Card,
  CardBody,
  CardHeader,
  Flex,
  Divider,
  Tabs,
  TabList,
  TabPanels,
  Tab,
  TabPanel,
  Grid,
  GridItem,
  Image,
  Button,
  ButtonGroup,
} from "@chakra-ui/react";
import { keyframes } from "@emotion/react";
import {
  FiTool,
  FiPackage,
  FiTruck,
  FiUser,
  FiHome,
  FiShield,
  FiDollarSign,
  FiClock,
  FiCheckCircle,
  FiXCircle,
  FiActivity,
  FiFileText,
  FiPaperclip,
  FiSettings,
} from "react-icons/fi";
import { MdBuild, MdLocationCity } from "react-icons/md";
import { BsGear } from "react-icons/bs";
import client from "../../../../helpers/Client";
import urlToId from "../../../../helpers/UrlToId";
import Product from "./Product";
import { useLoaderData, useRevalidator } from "react-router-dom";
import { ServiceState } from "../../../../components/States";
import Attachments from "../../../../components/attachments/Attachments";
import ServiceActions from "../../../../components/ServiceActions";
import { translateCoveredBy } from "../../../../shared/libs/translate";
import ServiceInfoBox from "../../../../components/ServiceInfoBox";
import Notes from "../../../../components/notes/Notes";
import Logs from "../../../../components/logs/Logs";
import InfoGrid from "../../../../components/InfoGrid";
import Link from "../../../../components/Link";

// Animations
const fadeIn = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
`;

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(30px); }
  to { opacity: 1; transform: translateY(0); }
`;

// Modern Info Card Component
function ModernServiceInfoCard({ title, value, icon, color = "blue", delay = 0 }) {
  const cardBg = "rgba(255, 255, 255, 0.8)";
  const borderColor = "gray.200";
  const textColor = "gray.600";
  
  return (
    <Card
      bg={cardBg}
      backdropFilter="blur(20px)"
      border="1px solid"
      borderColor={borderColor}
      borderRadius="xl"
      overflow="hidden"
      transition="all 0.3s ease"
      _hover={{
        transform: "translateY(-2px)",
        boxShadow: "lg",
        borderColor: `${color}.300`,
      }}
      animation={`${slideUp} 0.6s ease-out ${delay}s both`}
    >
      <CardBody p={4}>
        <VStack spacing={3} align="stretch">
          <HStack justify="space-between">
            <Icon as={icon} boxSize={5} color={`${color}.500`} />
          </HStack>
          <VStack align="stretch" spacing={1}>
            <Text fontSize="xs" color={textColor} fontWeight="medium">
              {title}
            </Text>
            <Box fontSize="sm" fontWeight="semibold" color={`${color}.600`}>
              {typeof value === 'string' ? (
                <Text>{value || "--"}</Text>
              ) : (
                value || <Text>--</Text>
              )}
            </Box>
          </VStack>
        </VStack>
      </CardBody>
    </Card>
  );
}

export async function serviceLoader({ params }) {
  const service = (await client.get(`/service_records/${params.serviceID}/`))
    .data;
  return [service];
}

export default function ServiceDetail() {
  const [service] = useLoaderData();
  const serviceID = urlToId(service.url);

  const [sentToBaseTrackingNumber, setSentToBaseTrackingNumber] = useState("");
  const [selectedCovared, setSelectedCovared] = useState("");
  const [selectedCargo, setSelectedCargo] = useState(null);

  const toast = useToast();
  const revalidator = useRevalidator();

  // Define color mode values at component level
  const cardBg = "rgba(255, 255, 255, 0.9)";
  const borderColor = "gray.200";

  const [basePrice, setBasePrice] = useState("0");
  const [discount, setDiscount] = useState("0");
  const [price, setPrice] = useState("0");
  const [serviceDesc, setServiceDesc] = useState('');
  const [errorData, setErrorData] = useState({});

  useEffect(() => {
    const discountedPrice = basePrice - (basePrice * discount) / 100;
    const formattedPrice = Math.min(discountedPrice, 99999999.99);
    const roundedPrice = Number.parseFloat(formattedPrice.toFixed(2));
    setPrice(roundedPrice);
  }, [discount, basePrice, setPrice]);

  const bgGradient = "linear(135deg, blue.50 0%, purple.50 25%, pink.50 50%, orange.50 75%, yellow.50 100%)";

  const handleAction = (apiPostfix, successMessage, extraParams) => {
    client
      .post(`/service_records/${serviceID}/${apiPostfix}/`, {
        ...extraParams,
        confirm: true,
      })
      .then(() => {
        toast({
          title: successMessage,
          status: "success",
        });
        revalidator.revalidate();
      })
      .catch((error) => {
        console.log(error);
        if (error.response && error.response.data) {
          setErrorData(error.response.data);
          Object.values(error.response.data.non_field_errors || []).forEach(
            (value) => {
              toast({
                title: "İşlem gerçekleştirilemiyor: " + value,
                status: "error",
              });
            }
          );
        }
      });
  };
  
  const handleDelete = () => handleAction("delete", "Servis Silindi.");
  const handleComplated = () => handleAction("reinstalled", "Servis Tamamlandı.");
  const handleFailed = () => handleAction("reinstall_failed", "Servis Yeniden İşleme Alındı.");
  const handleSendToBase = () => handleAction("sent_to_base", "Ürün servise gönderildi", {
    tracking_number: sentToBaseTrackingNumber,
    shipped_by: selectedCargo,
  });
  const handleReceivedByBase = () => handleAction("received_by_base", "Ürün servise ulaştı");
  const handleEvalStarted = () => handleAction("eval_started", "Ürün incelenmeye başlandı");
  const handleAccept = () => handleAction("coverage_accepted", "Ücret tutarı onaylandı");
  const handleRejected = () => handleAction("coverage_rejected", "Ücret tutarı reddedildi");
  const handleReceivedByReseller = () => handleAction("received_by_reseller", "Teslim alındı");
  const handleSendToReseller = () => handleAction("sent_to_reseller", "Bayiye gönderildi", {
    tracking_number: sentToBaseTrackingNumber,
    shipped_by: selectedCargo,
  });

  const handleEvalCompleted = () => handleAction("eval_completed", "Ücret tutarı belirlendi", {
    covered_by: selectedCovared,
    base_price: basePrice,
    discount_ratio: discount,
    service_desc: serviceDesc,
    price: price,
  });

  const handleReset = () => {
    setSentToBaseTrackingNumber("");
    setSelectedCovared("");
    setSelectedCargo(null);
    setBasePrice("0");
    setDiscount("0");
    setServiceDesc("");
    setErrorData({});
  };

  return (
    <Box
      minH="100vh"
      bgGradient={bgGradient}
      position="relative"
      overflow="hidden"
    >
      {/* Background Pattern */}
      <Box
        position="absolute"
        top="0"
        left="0"
        right="0"
        bottom="0"
        opacity={0.1}
        backgroundImage="radial-gradient(circle at 25% 25%, rgba(255,255,255,0.2) 0%, transparent 50%), radial-gradient(circle at 75% 75%, rgba(255,255,255,0.2) 0%, transparent 50%)"
        pointerEvents="none"
      />
      
      <Container maxW="container.xl" py={8}>
        <Grid templateColumns={{ base: "1fr", lg: "2fr 1fr" }} gap={8}>
          {/* Left Panel */}
          <GridItem>
            <VStack spacing={8} align="stretch">
              {/* Header Section */}
              <Card
                bg={cardBg}
                backdropFilter="blur(20px)"
                border="1px solid"
                borderColor={borderColor}
                borderRadius="2xl"
                overflow="hidden"
                animation={`${fadeIn} 0.6s ease-out`}
              >
                <CardHeader>
                  <Flex justify="space-between" align="center">
                    <HStack spacing={3}>
                      <Avatar
                        size="md"
                        icon={<Icon as={BsGear} />}
                        bg="blue.500"
                        color="white"
                      />
                      <VStack align="start" spacing={0}>
                        <Heading size="md" color="blue.600">
                          Servis Kaydı #{serviceID}
                        </Heading>
                        <Text fontSize="sm" color="gray.500">
                          Servis Takip Sistemi
                        </Text>
                      </VStack>
                    </HStack>
                    <ServiceState state={service.state} />
                  </Flex>
                </CardHeader>
                <CardBody pt={0}>
                  <VStack align="stretch" spacing={4}>
                    <Divider />
                    <Box>
                      <Text fontSize="sm" color="gray.600" mb={2} fontWeight="medium">
                        Servis Açıklaması:
                      </Text>
                      {service.desc.split('\n').map((line, i) => (
                        <Text key={'service-desc-line-' + i} mb={2} fontSize="md">
                          {line}
                        </Text>
                      ))}
                    </Box>
                  </VStack>
                </CardBody>
              </Card>
              
              {/* Service Info Cards */}
              <Card
                bg={cardBg}
                backdropFilter="blur(20px)"
                border="1px solid"
                borderColor={borderColor}
                borderRadius="2xl"
                overflow="hidden"
                animation={`${fadeIn} 0.8s ease-out 0.2s both`}
              >
                <CardHeader>
                  <HStack spacing={3}>
                    <Icon as={FiSettings} color="blue.500" boxSize={6} />
                    <Heading size="md">Servis Bilgileri</Heading>
                  </HStack>
                </CardHeader>
                <CardBody pt={0}>
                  <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4}>
                    <ModernServiceInfoCard
                      title="Kargo"
                      value={
                        service.sent_to_base_shipped_by ? (
                          <VStack align="start" spacing={1}>
                            <Text>{service.sent_to_base_shipped_by_display}</Text>
                            <Badge colorScheme="red" variant="subtle" borderRadius="full">
                              {service.sent_to_base_tracking_number}
                            </Badge>
                          </VStack>
                        ) : (
                          "Henüz Belirlenmedi"
                        )
                      }
                      icon={FiTruck}
                      color="orange"
                      delay={0.1}
                    />
                    <ModernServiceInfoCard
                      title="Bayi"
                      value={service.related_reseller?.name}
                      icon={FiUser}
                      color="purple"
                      delay={0.2}
                    />
                    <ModernServiceInfoCard
                      title="Proje Adı"
                      value={service.related_unit?.name || "Belirli Değil"}
                      icon={FiHome}
                      color="green"
                      delay={0.3}
                    />
                    <ModernServiceInfoCard
                      title="Garanti Durumu"
                      value={
                        service.related_unit?.exp_date === null
                          ? "Belirli değil"
                          : service.related_unit?.exp_date
                      }
                      icon={FiShield}
                      color="teal"
                      delay={0.4}
                    />
                    <ModernServiceInfoCard
                      title="Hasar Kapsamı"
                      value={translateCoveredBy(service.covered_by) || "Henüz belirlenmedi"}
                      icon={FiCheckCircle}
                      color="cyan"
                      delay={0.5}
                    />
                    <ModernServiceInfoCard
                      title="Servis Ücreti"
                      value={service.price ? "$" + service.price : "Henüz Belirlenmedi"}
                      icon={FiDollarSign}
                      color="pink"
                      delay={0.6}
                    />
                    <ModernServiceInfoCard
                      title="Yapılan İşlemler"
                      value={
                        service.service_desc ? (
                          <VStack align="start" spacing={1}>
                            {service.service_desc.split(/\r?\n/).map((line, i) => (
                              <Text key={i} fontSize="xs">{line}</Text>
                            ))}
                          </VStack>
                        ) : (
                          "Henüz işlem yapılmadı"
                        )
                      }
                      icon={FiTool}
                      color="red"
                      delay={0.7}
                    />
                  </SimpleGrid>
                </CardBody>
              </Card>
              
              {/* Service Actions */}
              <Card
                bg={cardBg}
                backdropFilter="blur(20px)"
                border="1px solid"
                borderColor={borderColor}
                borderRadius="2xl"
                overflow="hidden"
                animation={`${fadeIn} 1s ease-out 0.4s both`}
              >
                <CardHeader>
                  <HStack spacing={3}>
                    <Icon as={FiActivity} color="blue.500" boxSize={6} />
                    <Heading size="md">Servis İşlemleri</Heading>
                  </HStack>
                </CardHeader>
                <CardBody pt={0}>
                  <ServiceActions
                    errorData={errorData}
                    handleComplated={handleComplated}
                    handleFailed={handleFailed}
                    setSelectedCovared={setSelectedCovared}
                    setSelectedCargo={setSelectedCargo}
                    price={price}
                    setPrice={setPrice}
                    discount={discount}
                    setDiscount={setDiscount}
                    basePrice={basePrice}
                    setBasePrice={setBasePrice}
                    setServiceDesc={setServiceDesc}
                    setSentToBaseTrackingNumber={setSentToBaseTrackingNumber}
                    handleDelete={handleDelete}
                    handleSendToBase={handleSendToBase}
                    handleReceivedByBase={handleReceivedByBase}
                    handleReceivedByReseller={handleReceivedByReseller}
                    handleEvalStarted={handleEvalStarted}
                    handleEvalCompleted={handleEvalCompleted}
                    handleAccept={handleAccept}
                    handleSendToReseller={handleSendToReseller}
                    handleRejected={handleRejected}
                    handleReset={handleReset}
                    service={service}
                  />
                </CardBody>
              </Card>
              
              {/* Timeline and Additional Info */}
              <Card
                bg={cardBg}
                backdropFilter="blur(20px)"
                border="1px solid"
                borderColor={borderColor}
                borderRadius="2xl"
                overflow="hidden"
                animation={`${fadeIn} 1.2s ease-out 0.6s both`}
              >
                <CardBody p={0}>
                  <Tabs variant="enclosed" colorScheme="blue">
                    <TabList px={6} pt={6}>
                      <Tab>
                        <Icon as={FiClock} mr={2} />
                        Zaman Çizelgesi
                      </Tab>
                      <Tab>
                        <Icon as={FiFileText} mr={2} />
                        Notlar
                      </Tab>
                      <Tab>
                        <Icon as={FiPaperclip} mr={2} />
                        Ekler
                      </Tab>
                    </TabList>
                    
                    <TabPanels>
                      <TabPanel p={6}>
                        <InfoGrid
                          source={service}
                          keyMap={{
                            'created': 'Oluşturulma',
                            'sent_to_base': 'Servise Gönderilme',
                            'eval_started': 'İnceleme Başlangıç',
                            'eval_completed': 'İnceleme Bitiş',
                            'sent_to_reseller': 'Bayiye Gönderilme',
                            'receieved_by_reseller': 'Bayi Alındısı',
                            'coverage_accepted': 'Tamir Onayı',
                            'coverage_rejected': 'Tamir Reddi',
                            'received_by_reseller': 'Bayi Teslim Alma',
                          }}
                        />
                      </TabPanel>
                      <TabPanel p={6}>
                        <Notes
                          header={"Servis Kaydı Notları"}
                          modal_title={"İş emri #" + service.number}
                          object_model="ticket.servicerecord"
                          object_id={serviceID}
                        />
                      </TabPanel>
                      <TabPanel p={6}>
                        <Attachments object_model="ticket.servicerecord" object_id={serviceID} />
                      </TabPanel>
                    </TabPanels>
                  </Tabs>
                </CardBody>
              </Card>
            </VStack>
          </GridItem>
          
          {/* Right Panel */}
          <GridItem>
            <VStack spacing={6} align="stretch">
              {/* Product Info */}
              {service.related_model && (
                <Card
                  bg={cardBg}
                  backdropFilter="blur(20px)"
                  border="1px solid"
                  borderColor={borderColor}
                  borderRadius="2xl"
                  overflow="hidden"
                  animation={`${fadeIn} 0.8s ease-out 0.3s both`}
                >
                  <CardHeader>
                    <HStack spacing={3}>
                      <Icon as={FiPackage} color="blue.500" boxSize={5} />
                      <Heading size="sm">Ürün Bilgisi</Heading>
                    </HStack>
                  </CardHeader>
                  <CardBody pt={0}>
                    <Product
                      serviceImg={service.related_model.image}
                      serviceName={service.related_model.name}
                    />
                  </CardBody>
                </Card>
              )}
              
              {/* Related Task */}
              <Card
                bg={cardBg}
                backdropFilter="blur(20px)"
                border="1px solid"
                borderColor={borderColor}
                borderRadius="2xl"
                overflow="hidden"
                animation={`${fadeIn} 1s ease-out 0.5s both`}
              >
                <CardHeader>
                  <HStack spacing={3}>
                    <Icon as={FiFileText} color="blue.500" boxSize={5} />
                    <Heading size="sm">İlgili Alt Görev #{service.related_task.number}</Heading>
                  </HStack>
                </CardHeader>
                <CardBody pt={0}>
                  <Link
                    to={'/is-emri/' + service.related_task.ticket_id + '/?gorev=' + urlToId(service.related_task.url)}
                    fontWeight="semibold"
                    color="blue.600"
                    _hover={{ color: "blue.800" }}
                  >
                    {service.related_task.title}
                  </Link>
                </CardBody>
              </Card>
              
              {/* Activity Logs */}
              <Card
                bg={cardBg}
                backdropFilter="blur(20px)"
                border="1px solid"
                borderColor={borderColor}
                borderRadius="2xl"
                overflow="hidden"
                animation={`${fadeIn} 1.2s ease-out 0.7s both`}
              >
                <CardHeader>
                  <HStack spacing={3}>
                    <Icon as={FiActivity} color="blue.500" boxSize={5} />
                    <Heading size="sm">Aktivite Logları</Heading>
                  </HStack>
                </CardHeader>
                <CardBody pt={0}>
                  <Logs subjectType={'service_records'} subjectID={serviceID} />
                </CardBody>
              </Card>
            </VStack>
          </GridItem>
        </Grid>
      </Container>
    </Box>
  );
}
